/**
 * Forms engine API contracts (FORMS_DESIGN §11). Owner endpoints under /api/v1/forms, public
 * endpoints under /api/v1/public/f|s. The definition model itself lives in ../forms.ts.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, LocaleSchema, UuidSchema } from './common';
import { CampaignAudienceSchema } from './campaigns';
import { EmailDesignSchema } from '../emailDesign';
import {
  FORM_CHANNEL_PLANS,
  FORM_KINDS,
  FORM_RESPONSE_SOURCES,
  FORM_RESPONSE_STATUSES,
  FORM_STATUSES,
  FormDefinitionSchema,
  FormEventInputSchema,
  FormFieldSchema,
  FormPageSchema,
  FormSettingsSchema,
  FormTextsSchema,
  SEAT_STATUSES,
  SHARE_IDENTITY_MODES,
  WAITLIST_FLAGS,
} from '../forms';
import { FORM_TEMPLATE_KEYS } from '../formTemplates';

const FormKindSchema = z.enum(FORM_KINDS);
const StatusSchema = z.enum(FORM_RESPONSE_STATUSES);

// ─── owner: forms ────────────────────────────────────────────────────────────

export const FormListQuerySchema = z.object({
  kind: FormKindSchema,
  status: z.enum(['active', 'all', ...FORM_STATUSES]).default('active'),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});

export const CreateFormRequestSchema = z.object({
  kind: FormKindSchema,
  name: z.string().trim().min(1).max(120),
  title: z.string().trim().min(1).max(120),
  templateKey: z.enum(FORM_TEMPLATE_KEYS).optional(),
  locale: LocaleSchema.optional(),
});
export type CreateFormRequest = z.input<typeof CreateFormRequestSchema>;

export const UpdateFormRequestSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  title: z.string().trim().min(1).max(120).optional(),
  locale: LocaleSchema.optional(),
  definition: FormDefinitionSchema.optional(),
  settings: FormSettingsSchema.optional(),
  /** Event forms only. */
  event: FormEventInputSchema.optional(),
  opensAt: IsoDateTimeSchema.nullable().optional(),
  closesAt: IsoDateTimeSchema.nullable().optional(),
  /** Optimistic concurrency: the `updatedAt` the editor loaded (409 `stale_draft`). */
  expectedUpdatedAt: IsoDateTimeSchema,
  /** The AI design run this draft came from (provenance: `forms.ai_generated`, server-checked). */
  aiRunId: UuidSchema.optional(),
});
export type UpdateFormRequest = z.input<typeof UpdateFormRequestSchema>;

export const FormEventSchema = z.object({
  startsAt: IsoDateTimeSchema.nullable(),
  endsAt: IsoDateTimeSchema.nullable(),
  locationName: z.string(),
  address: z.string(),
  mapUrl: z.string().nullable(),
  onlineUrl: z.string().nullable(),
  capacity: z.number().int().nullable(),
  maxGuests: z.number().int(),
  shareMaxGuests: z.number().int(),
  allowMaybe: z.boolean(),
  rsvpDeadline: IsoDateTimeSchema.nullable(),
  cancelledAt: IsoDateTimeSchema.nullable(),
  cancelNote: z.string().nullable(),
  detailsRevision: z.number().int(),
  waitlistEnabled: z.boolean().default(false),
  checkinOpen: z.boolean().default(true),
});
export type FormEventDto = z.infer<typeof FormEventSchema>;

export const FormIssueSchema = z.object({
  code: z.string(),
  severity: z.enum(['error', 'warning']),
  fieldId: z.string().optional(),
});

export const FormCountersSchema = z.object({
  invited: z.number().int(),
  responses: z.number().int(),
  byStatus: z.record(z.string(), z.number().int()),
  seats: z
    .object({
      going: z.number().int(),
      capacity: z.number().int().nullable(),
      pendingApproval: z.number().int(),
    })
    .nullable(),
});

export const FormSummarySchema = z.object({
  id: UuidSchema,
  kind: FormKindSchema,
  status: z.enum(FORM_STATUSES),
  name: z.string(),
  title: z.string(),
  locale: LocaleSchema,
  publishedAt: IsoDateTimeSchema.nullable(),
  opensAt: IsoDateTimeSchema.nullable(),
  closesAt: IsoDateTimeSchema.nullable(),
  archivedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  createdBy: z.string().nullable(),
  shareEnabled: z.boolean(),
  event: z
    .object({
      startsAt: IsoDateTimeSchema.nullable(),
      cancelledAt: IsoDateTimeSchema.nullable(),
      capacity: z.number().int().nullable(),
    })
    .nullable(),
  counters: FormCountersSchema,
});
export type FormSummary = z.infer<typeof FormSummarySchema>;

export const FormListResponseSchema = z.object({
  items: z.array(FormSummarySchema),
  nextCursor: z.string().nullable(),
});

export const FormVersionSchema = z.object({
  id: UuidSchema,
  version: z.number().int(),
  definition: FormDefinitionSchema,
  publishedBy: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});

export const FormDetailSchema = FormSummarySchema.extend({
  definition: FormDefinitionSchema,
  settings: FormSettingsSchema,
  publishedVersion: FormVersionSchema.nullable(),
  eventDetails: FormEventSchema.nullable(),
  /** Readiness checklist (errors block publishing). */
  issues: z.array(FormIssueSchema),
  hasUnpublishedChanges: z.boolean(),
  needsAttention: z.array(
    z.object({
      code: z.string(),
      fieldId: z.string().optional(),
      fieldTarget: z.string().optional(),
    }),
  ),
  /** The public share link (derived; admin-visible), null when disabled. */
  shareUrl: z.string().nullable(),
  /** Field ids / `fieldId:optionId` pairs that already have answers (compatibility hints). */
  answered: z.object({ fieldIds: z.array(z.string()), optionKeys: z.array(z.string()) }),
  aiGenerated: z.boolean(),
});
export type FormDetail = z.infer<typeof FormDetailSchema>;

export const PublishFormResponseSchema = z.object({
  form: FormDetailSchema,
  version: z.number().int(),
  renameWarnings: z.array(
    z.object({
      fieldId: z.string(),
      optionId: z.string().optional(),
      from: z.string(),
      to: z.string(),
    }),
  ),
});

export const CancelEventRequestSchema = z.object({
  note: z.string().trim().max(300).optional(),
});

export const ShareSettingsRequestSchema = z.object({
  enabled: z.boolean(),
  rotate: z.boolean().default(false),
  identity: z.enum(SHARE_IDENTITY_MODES).optional(),
  maxPerDay: z.number().int().min(1).max(5000).optional(),
  approveSeats: z.boolean().nullable().optional(),
});

export const PreviewLinkRequestSchema = z.object({
  /** A version id, or 'draft' (default) to preview the unpublished draft. */
  versionId: z.union([UuidSchema, z.literal('draft')]).default('draft'),
  contactId: UuidSchema.nullable().default(null),
});

// ─── owner: responses ────────────────────────────────────────────────────────

export const CrmResultItemSchema = z.object({
  target: z.string(),
  fieldId: z.string().optional(),
  outcome: z.enum([
    'written',
    'noop',
    'conflict',
    'pending_verification',
    'email_taken',
    'skipped',
  ]),
  /** Masked for email/phone in API output. */
  before: z.unknown().nullable(),
  submitted: z.unknown().nullable(),
  reason: z.string().optional(),
  at: IsoDateTimeSchema,
  /** Set when an admin applied/rejected a suggestion or reverted a write. */
  resolution: z.enum(['applied', 'rejected', 'reverted']).optional(),
  /** Form tags this response added (tracked so status changes remove only our own). */
  tags: z.array(z.string()).optional(),
});
export type CrmResultItem = z.infer<typeof CrmResultItemSchema>;

export const FormResponseRowSchema = z.object({
  id: UuidSchema,
  contactId: UuidSchema.nullable(),
  contactName: z.string().nullable(),
  source: z.enum(FORM_RESPONSE_SOURCES),
  status: StatusSchema,
  guests: z.number().int(),
  seatStatus: z.enum(SEAT_STATUSES).nullable(),
  answers: z.record(z.string(), z.unknown()),
  verified: z.boolean(),
  needsReview: z.boolean(),
  firstSubmittedAt: IsoDateTimeSchema,
  lastSubmittedAt: IsoDateTimeSchema,
  revisionCount: z.number().int(),
  erased: z.boolean(),
  /** Share link only: identity as typed (staff-visible) and the suggested customer match. */
  identity: z
    .object({
      fullName: z.string().nullable(),
      email: z.string().nullable(),
      phone: z.string().nullable(),
    })
    .nullable(),
  suggestedContact: z.object({ id: UuidSchema, name: z.string().nullable() }).nullable(),
  linkBatchId: UuidSchema.nullable(),
  staffNote: z.string().nullable(),
  /** Events: waitlist position data and check-in (additive). */
  waitlistedAt: IsoDateTimeSchema.nullable().optional(),
  seatOfferExpiresAt: IsoDateTimeSchema.nullable().optional(),
  waitlistFlag: z.enum(WAITLIST_FLAGS).nullable().optional(),
  promotedAt: IsoDateTimeSchema.nullable().optional(),
  checkedInAt: IsoDateTimeSchema.nullable().optional(),
  checkedInGuests: z.number().int().nullable().optional(),
});
export type FormResponseRow = z.infer<typeof FormResponseRowSchema>;

export const FormResponsesQuerySchema = z.object({
  status: z.string().optional(),
  q: z.string().trim().max(100).optional(),
  field: z.string().optional(),
  value: z.string().max(200).optional(),
  source: z.enum(FORM_RESPONSE_SOURCES).optional(),
  needsReview: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  /** Comma-separated response ids («Εμφάνιση αυτών των απαντήσεων»). */
  ids: z.string().max(4000).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const FormRevisionSchema = z.object({
  id: UuidSchema,
  revisionNo: z.number().int(),
  versionId: UuidSchema,
  version: z.number().int(),
  source: z.enum(FORM_RESPONSE_SOURCES),
  status: StatusSchema,
  guests: z.number().int(),
  answers: z.record(z.string(), z.unknown()).nullable(),
  applied: z.boolean(),
  reason: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});

export const FormResponseDetailSchema = z.object({
  response: FormResponseRowSchema,
  crmResult: z.array(CrmResultItemSchema),
  revisions: z.array(FormRevisionSchema),
  /** Field labels per version id (each revision is rendered with its own version's labels). */
  versions: z.record(z.string(), z.array(FormFieldSchema)),
});

export const StaffResponseRequestSchema = z.object({
  contactId: UuidSchema,
  status: StatusSchema,
  guests: z.number().int().min(0).max(10).default(0),
  answers: z.record(z.string(), z.unknown()).default({}),
  note: z.string().trim().max(1000).optional(),
  /** Staff may exceed the capacity with an explicit confirmation («Πάνω από τη χωρητικότητα»). */
  allowOverCapacity: z.boolean().default(false),
});

export const PatchResponseRequestSchema = z
  .object({
    status: StatusSchema.optional(),
    guests: z.number().int().min(0).max(10).optional(),
    note: z.string().trim().max(1000).nullable().optional(),
    allowOverCapacity: z.boolean().default(false),
  })
  .refine((v) => v.status !== undefined || v.guests !== undefined || v.note !== undefined, {
    message: 'nothing_to_update',
  });

export const ResponseIdsRequestSchema = z.object({
  responseIds: z.array(UuidSchema).min(1).max(500),
});

export const ShareLinkRequestSchema = z.object({
  responseIds: z.array(UuidSchema).min(1).max(500),
  /** Declared lawful basis for contacts created from the answers (like imports). */
  lawfulBasis: z.enum(['consent', 'existing_customer', 'legitimate_interest']),
});

export const RemoveRecentRequestSchema = z.object({
  hours: z.number().int().min(1).max(168),
});

export const CrmChangesApplyRequestSchema = z.object({
  items: z
    .array(z.object({ responseId: UuidSchema, target: z.string().max(80) }))
    .min(1)
    .max(500),
});

export const NonRespondersQuerySchema = z.object({
  state: z.enum(['no_reply', 'not_reached', 'not_sent']).default('no_reply'),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

// ─── owner: sends (send groups) ──────────────────────────────────────────────

export const FormSendContentSchema = z.object({
  email: z
    .object({
      subject: z.string().trim().max(200).default(''),
      body: z.string().max(20000).default(''),
      design: EmailDesignSchema.nullable().default(null),
    })
    .optional(),
  sms: z.object({ body: z.string().max(2000) }).optional(),
});

export const FormSendRequestSchema = FormSendContentSchema.extend({
  name: z.string().trim().max(120).optional(),
  /** Invitation audience (all / tags / contacts / segment) or a follow-up by statuses. */
  audience: CampaignAudienceSchema.optional(),
  statuses: z.array(z.string()).min(1).max(8).optional(),
  channelPlan: z.enum(FORM_CHANNEL_PLANS).default('email_then_sms'),
  /** Remove people who already answered (default for invitations). */
  excludeResponded: z.boolean().optional(),
  locale: LocaleSchema.optional(),
  scheduledAt: IsoDateTimeSchema.optional(),
  /** Create the draft group only (the inline composer opens it). */
  draft: z.boolean().default(false),
  confirmHighVolume: z.boolean().default(false),
});
export type FormSendRequest = z.input<typeof FormSendRequestSchema>;

export const FormSendUpdateSchema = FormSendContentSchema.extend({
  name: z.string().trim().max(120).optional(),
  excludeResponded: z.boolean().optional(),
});

export const FormSendStartSchema = z.object({
  scheduledAt: IsoDateTimeSchema.optional(),
  confirmHighVolume: z.boolean().default(false),
});

// ─── contact card («Φόρμες & εκδηλώσεις») ─────────────────────────────────────

export const ContactFormsResponseSchema = z.object({
  /** This customer's answers (kinds in the plan only). */
  responses: z.array(
    z.object({
      responseId: UuidSchema,
      formId: UuidSchema,
      kind: FormKindSchema,
      title: z.string(),
      status: StatusSchema,
      guests: z.number().int(),
      seatStatus: z.enum(SEAT_STATUSES).nullable(),
      lastSubmittedAt: IsoDateTimeSchema,
    }),
  ),
  /** Invited (personal link sent or copied) and no answer yet, forms still open. */
  pending: z.array(
    z.object({
      formId: UuidSchema,
      kind: FormKindSchema,
      title: z.string(),
      lastInvitedAt: IsoDateTimeSchema.nullable(),
    }),
  ),
});
export type ContactFormsResponse = z.infer<typeof ContactFormsResponseSchema>;

// ─── stats ───────────────────────────────────────────────────────────────────

export const FormFieldStatSchema = z.object({
  fieldId: z.string(),
  key: z.string(),
  type: z.string(),
  label: z.string(),
  answered: z.number().int(),
  hidden: z.boolean(),
  sensitive: z.boolean(),
  options: z
    .array(z.object({ id: z.string(), label: z.string(), count: z.number().int() }))
    .optional(),
  other: z.number().int().optional(),
  rating: z
    .object({
      distribution: z.array(z.number().int()),
      average: z.number().nullable(),
      nps: z.number().nullable(),
    })
    .optional(),
  number: z
    .object({
      min: z.number().nullable(),
      p25: z.number().nullable(),
      median: z.number().nullable(),
      p75: z.number().nullable(),
      max: z.number().nullable(),
    })
    .optional(),
  date: z.array(z.object({ date: z.string(), count: z.number().int() })).optional(),
  yesNo: z.object({ yes: z.number().int(), no: z.number().int() }).optional(),
  text: z
    .object({
      count: z.number().int(),
      latest: z.array(z.object({ responseId: UuidSchema, excerpt: z.string(), at: z.string() })),
    })
    .optional(),
});

export const FormStatsSchema = z.object({
  invited: z.number().int(),
  delivered: z.number().int(),
  notReached: z.number().int(),
  notSent: z.record(z.string(), z.number().int()),
  opened: z.number().int(),
  responded: z.number().int(),
  noReply: z.number().int(),
  byStatus: z.record(z.string(), z.number().int()),
  bySource: z.record(z.string(), z.number().int()),
  seats: z
    .object({
      going: z.number().int(),
      capacity: z.number().int().nullable(),
      overbooked: z.boolean(),
      pendingApproval: z.number().int(),
      waitlistSeats: z.number().int(),
      /** Seats held by an open waitlist offer (part of `waitlistSeats`; not yet `going`). */
      offeredSeats: z.number().int(),
      checkedIn: z.number().int(),
      checkedInSeats: z.number().int(),
    })
    .nullable(),
  guestsTotal: z.number().int().nullable(),
  daily: z.array(z.object({ date: z.string(), responses: z.number().int() })),
  fields: z.array(FormFieldStatSchema),
  crosstabs: z.array(
    z.object({
      fieldId: z.string(),
      rows: z.array(
        z.object({
          status: z.string(),
          total: z.number().int(),
          options: z.array(z.object({ id: z.string(), count: z.number().int() })),
        }),
      ),
    }),
  ),
  pending: z.object({
    crmChanges: z.number().int(),
    needsReview: z.number().int(),
    shareUnlinked: z.number().int(),
    seatApprovals: z.number().int(),
  }),
});
export type FormStats = z.infer<typeof FormStatsSchema>;

// ─── public ──────────────────────────────────────────────────────────────────

export const PUBLIC_FORM_STATES = [
  'open',
  'not_open_yet',
  'closed',
  'cancelled',
  'deadline',
  'full',
] as const;
export type PublicFormState = (typeof PUBLIC_FORM_STATES)[number];

export const PublicCrmInputSchema = z.object({
  /** Widget for a CRM-linked field (derived from the target / custom field type). */
  input: z.enum([
    'text',
    'textarea',
    'email',
    'tel',
    'date',
    'number',
    'boolean',
    'select',
    'multi_select',
    'url',
  ]),
  options: z.array(z.string()).optional(),
  /** Current value shown for editing (names / non-personal custom fields only, when allowed). */
  prefill: z.unknown().nullable(),
  /** «Έχουμε καταχωρημένο: m•••@gmail.com» (never the full value). */
  maskedHint: z.string().nullable(),
});

export const PublicFormPayloadSchema = z.object({
  mode: z.enum(['personal', 'share', 'preview']),
  formId: UuidSchema,
  kind: FormKindSchema,
  locale: LocaleSchema,
  title: z.string(),
  versionId: z.string(),
  business: z.object({ name: z.string(), logoUrl: z.string().nullable() }),
  privacy: z.object({
    url: z.string().nullable(),
    notice: z.object({
      controller: z.string(),
      address: z.string().nullable(),
      email: z.string().nullable(),
      phone: z.string().nullable(),
      fields: z.array(z.string()),
      retentionDays: z.number().int().nullable(),
    }),
  }),
  page: FormPageSchema,
  texts: FormTextsSchema,
  /** Fields to render (CRM fields already filtered for this person; client evaluates showIf). */
  fields: z.array(FormFieldSchema),
  crmInputs: z.record(z.string(), PublicCrmInputSchema),
  consentStatements: z.record(z.string(), z.string()),
  primaryLabels: z.record(z.string(), z.string()),
  /** Share link: which identity inputs to ask for. */
  identity: z.object({ mode: z.enum(SHARE_IDENTITY_MODES) }).nullable(),
  event: z
    .object({
      startsAt: IsoDateTimeSchema.nullable(),
      endsAt: IsoDateTimeSchema.nullable(),
      timezone: z.string(),
      locationName: z.string(),
      address: z.string(),
      mapUrl: z.string().nullable(),
      /** Only after a positive answer. */
      onlineUrl: z.string().nullable(),
      allowMaybe: z.boolean(),
      maxGuests: z.number().int(),
      rsvpDeadline: IsoDateTimeSchema.nullable(),
      cancelled: z.boolean(),
      cancelNote: z.string().nullable(),
      seatsLeft: z.number().int().nullable(),
      /** Full event: a `going` answer joins the waitlist instead of being refused. */
      waitlistEnabled: z.boolean().default(false),
    })
    .nullable(),
  state: z.enum(PUBLIC_FORM_STATES),
  allowChanges: z.boolean(),
  response: z
    .object({
      status: StatusSchema,
      guests: z.number().int(),
      answers: z.record(z.string(), z.unknown()),
      seatStatus: z.enum(SEAT_STATUSES).nullable(),
      submittedAt: IsoDateTimeSchema,
      canChange: z.boolean(),
      /** A freed seat is held for this person until then («Βρέθηκε θέση — επιβεβαιώστε»). */
      seatOfferExpiresAt: IsoDateTimeSchema.nullable().default(null),
      remindersOptOut: z.boolean().default(false),
    })
    .nullable(),
  respondent: z.object({ firstName: z.string().nullable() }).nullable(),
  /** Personal links only: the person opted out of email/SMS (link to the preferences page). */
  notices: z.object({ emailUnsubscribed: z.boolean(), smsUnsubscribed: z.boolean() }).nullable(),
  /** Signed render stamp (share link: sent back on submit). */
  stamp: z.string(),
  /** Calendar file URL (events). */
  icsUrl: z.string().nullable(),
});
export type PublicFormPayload = z.infer<typeof PublicFormPayloadSchema>;

export const PublicSubmitRequestSchema = z.object({
  submissionKey: UuidSchema,
  versionId: z.string().max(64),
  answers: z.record(z.string(), z.unknown()).default({}),
  stamp: z.string().max(400).optional(),
});
export type PublicSubmitRequest = z.input<typeof PublicSubmitRequestSchema>;

export const PublicShareSubmitRequestSchema = PublicSubmitRequestSchema.extend({
  identity: z.object({
    fullName: z.string().max(200).default(''),
    email: z.string().max(254).optional(),
    phone: z.string().max(40).optional(),
  }),
  stamp: z.string().max(400),
  /** Honeypot (visually hidden): any value → silent drop. */
  website: z.string().max(500).optional(),
});
export type PublicShareSubmitRequest = z.input<typeof PublicShareSubmitRequestSchema>;

export const PublicSubmitResponseSchema = z.object({
  status: StatusSchema,
  guests: z.number().int(),
  seatStatus: z.enum(SEAT_STATUSES).nullable(),
  replayed: z.boolean(),
  thankYou: z.string(),
  canChange: z.boolean(),
  /** Event: the online link once the answer is positive. */
  onlineUrl: z.string().nullable(),
});
export type PublicSubmitResponse = z.infer<typeof PublicSubmitResponseSchema>;

// ─── AI: page design (purpose `form_design`, FORMS_DESIGN §6.5) ──────────────

export const AiFormDesignRequestSchema = z.object({
  prompt: z.string().trim().min(3).max(1500),
  /** Earlier requests of this design session (oldest first). */
  history: z.array(z.string().max(1500)).max(6).default([]),
  /** The draft on screen (may be unsaved); defaults to the stored draft. */
  current: FormDefinitionSchema.optional(),
  /** Client request id (a retried request never runs — or bills — twice). */
  requestId: z.string().min(8).max(100).optional(),
});
export type AiFormDesignRequest = z.input<typeof AiFormDesignRequestSchema>;

export const FORM_EVENT_SUGGESTION_FIELDS = [
  'date',
  'time',
  'location',
  'address',
  'capacity',
  'maxGuests',
] as const;
export type FormEventSuggestionField = (typeof FORM_EVENT_SUGGESTION_FIELDS)[number];

export const AiFormDesignResponseSchema = z.object({
  /** The converted draft (nothing is saved: the editor applies it, saving is a normal PUT). */
  definition: FormDefinitionSchema,
  title: z.string(),
  summary: z.string(),
  /** Event logistics are never applied by the AI: the owner confirms each suggestion. */
  eventSuggestions: z.array(
    z.object({ field: z.enum(FORM_EVENT_SUGGESTION_FIELDS), value: z.string() }),
  ),
  /** Links/images the model invented (dropped). */
  removed: z.array(z.string()),
  /** What the server changed in the model output (e.g. `crm_unknown`, `consent_replaced`). */
  adjustments: z.array(z.object({ code: z.string(), label: z.string().optional() })),
  creditsUsed: z.number().int(),
  runId: UuidSchema,
});
export type AiFormDesignResponse = z.infer<typeof AiFormDesignResponseSchema>;

// ─── AI: questions over the answers (purpose `form_insights`, §13) ───────────

export const AskFormRequestSchema = z.object({
  question: z.string().trim().min(3).max(500),
  history: z
    .array(z.object({ q: z.string().max(500), a: z.string().max(2500) }))
    .max(3)
    .default([]),
  requestId: z.string().min(8).max(100).optional(),
});
export type AskFormRequest = z.input<typeof AskFormRequestSchema>;

export const AskFormGroupSchema = z.object({
  id: z.string(),
  label: z.string(),
  /** Counted by the server from the refs the model listed (never a model number). */
  count: z.number().int(),
  responseIds: z.array(UuidSchema),
});

export const AskFormCitationSchema = z.object({
  responseId: UuidSchema.nullable(),
  /** Verbatim substring of the (redacted) answer, or «[διαγράφηκε]» after an erasure. */
  quote: z.string(),
});

export const AskFormResultSchema = z.object({
  runId: UuidSchema,
  question: z.string(),
  answer: z.string(),
  groups: z.array(AskFormGroupSchema),
  citations: z.array(AskFormCitationSchema),
  followUps: z.array(z.string()),
  insufficientData: z.boolean(),
  /** A placeholder the server could not ground was removed. */
  ungrounded: z.boolean(),
  /** The dataset was a sample of the answers (token budget). */
  sample: z.object({ included: z.number().int(), total: z.number().int() }).nullable(),
  creditsUsed: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type AskFormResult = z.infer<typeof AskFormResultSchema>;

export const AskFormHistorySchema = z.object({ items: z.array(AskFormResultSchema) });

// ─── events: reminders (§7.4) ────────────────────────────────────────────────

export const FORM_REMINDER_KINDS = ['attendee', 'non_responder'] as const;
export type FormReminderKind = (typeof FORM_REMINDER_KINDS)[number];
export const FORM_REMINDER_STATUSES = [
  'scheduled',
  'processing',
  'sent',
  'skipped',
  'cancelled',
] as const;
export type FormReminderStatus = (typeof FORM_REMINDER_STATUSES)[number];
export const MAX_ATTENDEE_REMINDERS = 3;
export const MAX_NON_RESPONDER_REMINDERS = 2;
/** Attendee reminder presets (minutes before the start). */
export const REMINDER_OFFSET_PRESETS = [7 * 24 * 60, 24 * 60, 3 * 60] as const;

const ReminderContentSchema = z.object({
  channelPlan: z.enum(FORM_CHANNEL_PLANS).default('email_then_sms'),
  email: z
    .object({ subject: z.string().trim().max(200), body: z.string().trim().max(5000) })
    .optional(),
  sms: z.object({ body: z.string().trim().max(1000) }).optional(),
});

export const CreateFormReminderRequestSchema = ReminderContentSchema.extend({
  kind: z.enum(FORM_REMINDER_KINDS),
  /** Attendee reminders: minutes before the event start (15 min … 30 days). */
  offsetMinutes: z
    .number()
    .int()
    .min(15)
    .max(30 * 24 * 60)
    .optional(),
  /** Non-responder reminders: an absolute instant before the deadline. */
  sendAt: IsoDateTimeSchema.optional(),
  /** Attendee reminders: also people who answered «ίσως». */
  includeMaybe: z.boolean().default(false),
  /** The admin confirms a large send at configuration time (§7.4). */
  confirmHighVolume: z.boolean().default(false),
});
export type CreateFormReminderRequest = z.input<typeof CreateFormReminderRequestSchema>;

export const UpdateFormReminderRequestSchema = ReminderContentSchema.partial().extend({
  offsetMinutes: z
    .number()
    .int()
    .min(15)
    .max(30 * 24 * 60)
    .optional(),
  sendAt: IsoDateTimeSchema.optional(),
  includeMaybe: z.boolean().optional(),
  confirmHighVolume: z.boolean().optional(),
});

export const FormReminderSchema = z.object({
  id: UuidSchema,
  kind: z.enum(FORM_REMINDER_KINDS),
  offsetMinutes: z.number().int().nullable(),
  sendAt: IsoDateTimeSchema,
  statuses: z.array(z.string()),
  channelPlan: z.enum(FORM_CHANNEL_PLANS),
  email: z.object({ subject: z.string(), body: z.string() }).nullable(),
  sms: z.object({ body: z.string() }).nullable(),
  status: z.enum(FORM_REMINDER_STATUSES),
  skipReason: z.string().nullable(),
  sendGroupId: UuidSchema.nullable(),
  confirmedHighVolume: z.boolean(),
  /** Recipients and credits at configuration time. */
  estimate: z
    .object({ recipients: z.number().int(), email: z.number().int(), sms: z.number().int() })
    .nullable(),
  processedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type FormReminder = z.infer<typeof FormReminderSchema>;

export const FormRemindersResponseSchema = z.object({
  items: z.array(FormReminderSchema),
  /** Default copy for a new reminder (el/en by form locale). */
  defaults: z.object({
    attendee: z.object({ subject: z.string(), email: z.string(), sms: z.string() }),
    non_responder: z.object({ subject: z.string(), email: z.string(), sms: z.string() }),
  }),
});

// ─── events: check-in (§7.5) and waitlist (§7.2) ─────────────────────────────

export const CheckInEntrySchema = z.object({
  responseId: UuidSchema,
  contactId: UuidSchema.nullable(),
  name: z.string().nullable(),
  /** Last 4 digits only (search by phone without exposing it). */
  phoneLast4: z.string().nullable(),
  status: StatusSchema,
  seatStatus: z.enum(SEAT_STATUSES).nullable(),
  guests: z.number().int(),
  source: z.enum(FORM_RESPONSE_SOURCES),
  checkedInAt: IsoDateTimeSchema.nullable(),
  checkedInGuests: z.number().int().nullable(),
});
export type CheckInEntry = z.infer<typeof CheckInEntrySchema>;

export const CheckInListSchema = z.object({
  items: z.array(CheckInEntrySchema),
  counters: z.object({
    /** Seats arrived (people + arrived guests) of confirmed `going` answers. */
    arrivedSeats: z.number().int(),
    expectedSeats: z.number().int(),
    arrivedParties: z.number().int(),
    expectedParties: z.number().int(),
    capacity: z.number().int().nullable(),
  }),
  checkinOpen: z.boolean(),
});
export type CheckInList = z.infer<typeof CheckInListSchema>;

export const CheckInRequestSchema = z.object({
  checkedIn: z.boolean(),
  /** Guests who came with the person (0 … guests). */
  guestsArrived: z.number().int().min(0).max(10).default(0),
});

export const WalkInRequestSchema = z
  .object({
    /** An existing customer the staff picked (search) … */
    contactId: UuidSchema.optional(),
    /** … or a new one (name required; phone/email optional). */
    contact: z
      .object({
        firstName: z.string().trim().min(1).max(100),
        lastName: z.string().trim().max(100).default(''),
        phone: z.string().trim().max(40).optional(),
        email: z.string().trim().max(254).optional(),
        lawfulBasis: z.enum(['consent', 'existing_customer', 'legitimate_interest']),
      })
      .optional(),
    guests: z.number().int().min(0).max(10).default(0),
  })
  .refine((v) => Boolean(v.contactId) !== Boolean(v.contact), { message: 'contact_or_new' });

export const PromoteRequestSchema = z.object({
  /** Promote although the event is full («Πάνω από τη χωρητικότητα»). */
  allowOverCapacity: z.boolean().default(false),
  /** Tell the person (email, else SMS) when they may be messaged. */
  notify: z.boolean().default(true),
});
